import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import Portal from './Portal';
import api from '../services/api';

const TaskModal = ({ isOpen, onClose, onSubmit, initialData, date }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: date || new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    status: 'Pending',
    color: 'blue',
    project_id: '',
    assignees: []
  });

  const [error, setError] = useState('');
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Use useCallback to memoize the function and fix the dependency issue
  const loadProjectsAndUsers = useCallback(async () => {
    setLoading(true);
    console.log('TaskModal: Starting to load projects and users...');
    
    try {
      // Load projects - fix the endpoint path
      console.log('TaskModal: Loading projects...');
      const projectsResponse = await api.get('/project/projet_name');
      console.log('TaskModal: Projects response:', projectsResponse);
      
      if (projectsResponse.data && projectsResponse.data.data) {
        setProjects(projectsResponse.data.data);
        console.log('TaskModal: Projects loaded successfully:', projectsResponse.data.data.length);
        
        // Set default project if none selected
        if (!formData.project_id && projectsResponse.data.data.length > 0) {
          setFormData(prev => ({
            ...prev,
            project_id: projectsResponse.data.data[0].id
          }));
          console.log('TaskModal: Set default project:', projectsResponse.data.data[0].id);
        }
      }

      // Try to load users - handle permissions gracefully
      console.log('TaskModal: Attempting to load users...');
      try {
        const usersResponse = await api.get('/user/user/paginated_list?page=1&per_page=100&time_zone=UTC');
        console.log('TaskModal: Users response:', usersResponse);
        
        if (usersResponse.data && usersResponse.data.data) {
          setUsers(usersResponse.data.data);
          console.log('TaskModal: Users loaded successfully:', usersResponse.data.data.length);
        }
      } catch (userError) {
        console.warn('TaskModal: Could not load users via admin endpoint:', userError);
        
        // Check if it's a permission error
        if (userError.response && userError.response.status === 403) {
          console.log('TaskModal: Permission denied for user list, trying current user profile...');
          
          // Alternative: Load current user profile as fallback
          try {
            const profileResponse = await api.get('/auth/profile');
            console.log('TaskModal: Profile response:', profileResponse);
            
            if (profileResponse.data) {
              const currentUser = profileResponse.data;
              // Create a user list with just the current user
              const userList = [{
                id: currentUser.id || 1,
                first_name: currentUser.first_name || currentUser.username || 'Current',
                last_name: currentUser.last_name || 'User',
                email: currentUser.email || 'current@user.com',
                username: currentUser.username || 'current_user'
              }];
              setUsers(userList);
              console.log('TaskModal: Using current user as fallback:', userList);
            } else {
              console.log('TaskModal: No profile data received');
              setUsers([]);
            }
          } catch (profileError) {
            console.warn('TaskModal: Could not load current user profile:', profileError);
            setUsers([]);
          }
        } else {
          // Other error (network, etc.)
          console.error('TaskModal: Network or other error loading users:', userError);
          setUsers([]);
        }
      }
    } catch (error) {
      console.error('TaskModal: Failed to load projects:', error);
      setError('Failed to load projects. Please try again.');
    } finally {
      setLoading(false);
      console.log('TaskModal: Finished loading projects and users');
    }
  }, []); // Remove formData.project_id dependency to prevent infinite loops

  // Reset form when modal opens or initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        description: initialData.description || '',
        due_date: initialData.due_date || date || new Date().toISOString().split('T')[0],
        time: initialData.time || new Date().toTimeString().slice(0, 5),
        status: initialData.status || 'Pending',
        color: initialData.color || 'blue',
        project_id: initialData.project_id || '',
        assignees: initialData.assignees || []
      });
    } else {
      setFormData({
        title: '',
        description: '',
        due_date: date || new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().slice(0, 5),
        status: 'Pending',
        color: 'blue',
        project_id: '',
        assignees: []
      });
    }
    setError('');
  }, [isOpen, initialData, date]);

  // Load projects and users when modal opens
  useEffect(() => {
    if (isOpen) {
      loadProjectsAndUsers();
    }
  }, [isOpen, loadProjectsAndUsers]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAssigneeChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => parseInt(option.value));
    setFormData(prev => ({
      ...prev,
      assignees: selectedOptions
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    
    if (!formData.due_date) {
      setError('Date is required');
      return;
    }
    
    if (!formData.time) {
      setError('Time is required');
      return;
    }

    if (!formData.project_id) {
      setError('Project is required');
      return;
    }
    
    // Map status strings to integers as expected by Flask backend
    const statusMapping = {
      'Pending': 1,        // Open
      'In Progress': 2,    // Inprogress  
      'Completed': 4       // Closed
    };
    
    // Map color strings to integers
    const colorMapping = {
      'blue': 1,
      'green': 2,
      'red': 3,
      'purple': 4,
      'orange': 5
    };
    
    // Create a formatted copy of the data matching Flask backend expectations
    const formattedData = {
      name: formData.title.trim(),
      description: formData.description || '',
      due_date: formData.due_date,
      status: statusMapping[formData.status] || 1,
      color: colorMapping[formData.color] || 1,
      add_members: formData.assignees.length > 0 ? formData.assignees : [],
      project_id: formData.project_id  // Include for API service to extract for URL
    };
    
    console.log('TaskModal submitting data:', formattedData);
    console.log('Original form data:', formData);
    
    // Check if due_date is in correct format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(formattedData.due_date)) {
      try {
        // Try to format the due_date if it's not in the correct format
        const dateObj = new Date(formattedData.due_date);
        formattedData.due_date = dateObj.toISOString().split('T')[0];
      } catch (error) {
        setError('Invalid due_date format. Please use YYYY-MM-DD.');
        return;
      }
    }
    
    onSubmit(formattedData);
  };

  // Don't render anything if modal is closed
  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="close-modal" onClick={onClose} aria-label="Close modal">&times;</button>
          <h2 id="modal-title">{initialData ? 'Edit Task' : 'Add New Task'}</h2>
          
          {error && <div className="error-message">{error}</div>}
          
          {loading && <div className="loading-message">Loading projects and users...</div>}
          
          <form id="task-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="title">Task Title</label>
              <input 
                type="text" 
                id="title" 
                name="title" 
                value={formData.title}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea 
                id="description" 
                name="description" 
                value={formData.description}
                onChange={handleChange}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="due_date">Date</label>
              <input 
                type="date" 
                id="due_date" 
                name="due_date" 
                value={formData.due_date}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="time">Time</label>
              <input 
                type="time" 
                id="time" 
                name="time" 
                value={formData.time}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="project_id">Project</label>
              <select 
                id="project_id" 
                name="project_id" 
                value={formData.project_id}
                onChange={handleChange}
                required
              >
                <option value="">Select a project</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            {/* {users.length > 0 && (
              <div className="form-group">
                <label htmlFor="assignees">Assign to (hold Ctrl/Cmd to select multiple)</label>
                <select 
                  id="assignees" 
                  name="assignees" 
                  multiple
                  value={formData.assignees}
                  onChange={handleAssigneeChange}
                  style={{ minHeight: '100px' }}
                >
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.email})
                    </option>
                  ))}
                </select>
                <small style={{ color: '#666', fontSize: '0.875rem' }}>
                  Hold Ctrl (Windows) or Cmd (Mac) to select multiple assignees
                </small>
              </div>
            )} */}

              {users.length > 0 && (
                <div className="form-group">
                  <label htmlFor="assignees">Assign to</label>
                  <Select
                    id="assignees"
                    isMulti
                    options={users.map(user => ({
                      value: user.id,
                      label: `${user.first_name} ${user.last_name} (${user.email})`
                    }))}
                    value={users
                      .filter(user => formData.assignees.includes(user.id))
                      .map(user => ({
                        value: user.id,
                        label: `${user.first_name} ${user.last_name} (${user.email})`
                      }))}
                    onChange={(selectedOptions) =>
                      setFormData(prev => ({
                        ...prev,
                        assignees: selectedOptions.map(option => option.value)
                      }))
                    }
                  />
                </div>
              )}

            {users.length === 0 && !loading && (
              <div className="form-group">
                <p style={{ color: '#666', fontSize: '0.875rem', fontStyle: 'italic' }}>
                  User assignment not available. The task will be assigned to you by default.
                </p>
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select 
                id="status" 
                name="status" 
                value={formData.status}
                onChange={handleChange}
                required
              >
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="color">Color</label>
              <select 
                id="color" 
                name="color" 
                value={formData.color}
                onChange={handleChange}
              >
                <option value="blue">Blue</option>
                <option value="green">Green</option>
                <option value="red">Red</option>
                <option value="purple">Purple</option>
                <option value="orange">Orange</option>
              </select>
            </div>
            
            <button type="submit" className="submit-btn" disabled={loading}>
              {initialData ? 'Update Task' : 'Add Task'}
            </button>
          </form>
        </div>
      </div>
    </Portal>
  );
};

export default TaskModal; 